<template>
  <transition
    name="modal-fade"
    @after-enter="afterEnter"
    @after-leave="afterLeave"
  >
    <div
      v-show="visible"
      class="plv-ia-c-modal"
      :class="{
        'plv-ia-c-modal--no-bg': noBg
      }"
      @click.self="handleWrapperClick"
      @mousedown.self="handleBgMousedown"
    >
      <div
        ref="modal"
        class="plv-ia-c-modal__content pws-vclass-c-modal__content"
        :style="computedStyle"
      >
        <div
          ref="header"
          class="plv-ia-c-modal__header"
          :style="{
            cursor: draggable ? 'move' : ''
          }"
        >
          <h2>{{ title }}<span v-if="subTitle" :style="subTitleStyle">{{ subTitle }}</span></h2>
          <button v-if="closable" class="plv-ia-c-modal__close" @click="handleClose"></button>
        </div>
        <div
          ref="body"
          class="plv-ia-c-modal__body"
          :class="{
            'plv-ia-c-modal__body--border': overflowBorderBottom,
          }"
          :style="bodyHeight ? {
            height: `${bodyHeight}px`,
          } : {}"
        >
          <slot></slot>
        </div>
        <div
          ref="footer"
          class="plv-ia-c-modal__footer"
        >
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'CommonModal',
  props: {
    appendToBody: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    closeOnClickModal: {
      type: Boolean,
      default: true,
    },
    modalStyle: {
      type: Object,
      default: null,
    },
    setModalPosition: {
      type: Function,
      default: null,
    },
    closable: {
      type: Boolean,
      default: true,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    subTitle: {
      type: String,
      default: '',
    },
    subTitleStyle: {
      type: Object,
      default() {
        return {};
      },
    },
    size: {
      type: String,
      default: '',
    },
    // 没有背景遮罩层
    noBg: {
      type: Boolean,
    },
    // body 最大高度，设置之后，纵向溢出会有固定样式的滚动条、底部有边框
    bodyHeight: {
      type: Number,
      default: 0,
    },
    bodyLocked: {
      type: Boolean,
      default: false,
    },
    rememberLastPosition: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      closed: false,
      marginTop: 0,
      marginLeft: -280,
      paddingBottom: 0, // 空出footer的位置
      isDragging: false,
      dragPointX: 0,
      dragPointY: 0,
      isBgMouseDown: false, // 用于防止拖拽超出弹窗后释放鼠标触发背景点击事件
      modalPositionData: null,
      overflowBorderBottom: false, // 纵向溢出并显示底部边框
      hasDragged: false, // 是否拖动过，拖动过之后视图更新不再调用 setModalPosition 来定位。
    };
  },

  computed: {
    computedStyle() {
      let style = {
        marginTop: `${this.marginTop}px`,
        marginLeft: `${this.marginLeft}px`,
        paddingBottom: `${this.paddingBottom}px`,
      };
      style = Object.assign({}, style, this.modalStyle, this.modalPositionData);
      return style;
    },
  },

  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        if (typeof newVal !== 'boolean') return;
        if (newVal) {
          this.closed = false;
          this.$emit('open');
          if (this.appendToBody) {
            document.body.appendChild(this.$el);
          }
          this.updateBasicStyle();
          if (this.bodyLocked) {
            this.lockDocBody();
          }
        } else {
          if (!this.closed) this.$emit('close', newVal);
          this.isBgMouseDown = false;
          if (this.bodyLocked) {
            this.unlockDocBody();
          }
        }
      }
    },

    modalStyle() {
      this.updateBasicStyle();
    },

    isDragging(newVal) {
      if (newVal) {
        this.hasDragged = true;
      }
    },
  },

  mounted() {
    if (this.visible && this.appendToBody) {
      document.body.appendChild(this.$el);
    }
    this.updateBasicStyle();
    if (this.draggable) {
      document.addEventListener('mousedown', this.handleMouseDown);
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    }
  },

  destroyed() {
    if (this.bodyLocked) {
      this.unlockDocBody();
    }
    if (this.appendToBody && this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
    if (this.draggable) {
      document.removeEventListener('mousedown', this.handleMouseDown);
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    }
  },

  methods: {
    handleBgMousedown() {
      this.isBgMouseDown = true;
    },
    hide(cancel) {
      if (cancel) { return; }
      this.$emit('update:visible', false);
      this.$emit('close', 1);
      this.closed = true;
    },
    handleWrapperClick() {
      if (!this.closeOnClickModal) { return; }
      if (!this.isBgMouseDown) { return; }
      this.handleClose();
    },
    handleClose() {
      if (typeof this.beforeClose === 'function') {
        this.beforeClose(this.hide);
      } else {
        this.hide();
      }
    },
    afterEnter() {
      this.$emit('opened');
    },
    afterLeave() {
      this.$emit('closed');
    },
    updateBasicStyle(resetPosition) {
      resetPosition = resetPosition || (!this.hasDragged || !this.rememberLastPosition);
      this.$nextTick(() => {
        const modalEl = this.$refs.modal;
        const footerEl = this.$refs.footer;
        if (resetPosition) {
          this.marginTop = -modalEl.offsetHeight / 2;
          this.marginLeft = -modalEl.offsetWidth / 2;
        }
        this.paddingBottom = footerEl.offsetHeight;

        // 特殊定位函数
        if (resetPosition && this.setModalPosition) {
          const { left, top } = (this.setModalPosition(this.$refs.modal)) || {};
          const modalEl = this.$refs.modal;
          const modalPositionData = {};
          if (typeof left !== 'undefined') {
            modalPositionData.left = `${left + modalEl.offsetWidth / 2}px`;
          }
          if (typeof top !== 'undefined') {
            modalPositionData.top = `${top + modalEl.offsetHeight / 2}px`;
          }
          this.modalPositionData = modalPositionData;
        }

        // 判断是否需要加上底部边框
        const bodyEl = this.$refs.body;
        if (this.bodyHeight && bodyEl && bodyEl.scrollHeight > this.bodyHeight) {
          this.overflowBorderBottom = true;
        } else {
          this.overflowBorderBottom = false;
        }
      });
    },
    handleMouseDown(e) {
      if (!this.$refs.header.contains(e.target)) { return; }
      this.isDragging = true;
      this.dragPointX = e.pageX;
      this.dragPointY = e.pageY;
    },
    handleMouseUp() {
      if (!this.draggable) { return; }
      this.isDragging = false;
    },
    handleMouseMove(e) {
      if (!this.draggable || !this.isDragging) { return; }
      const modalEl = this.$refs.modal;

      const offsetY = e.pageY - this.dragPointY;
      const offsetX = e.pageX - this.dragPointX;
      this.dragPointX = e.pageX;
      this.dragPointY = e.pageY;

      // 左右不超出边界
      if ((modalEl.offsetWidth + modalEl.offsetLeft >= document.body.offsetWidth && offsetX > 0) ||
        (modalEl.offsetLeft <= 0 && offsetX < 0)) {
        return;
      }
      if (modalEl.offsetHeight + modalEl.offsetTop >= document.body.offsetHeight && offsetY > 0) {
        return;
      }
      if (modalEl.offsetTop <= 0 && offsetY < 0) {
        return;
      }

      this.marginTop += offsetY;
      this.marginLeft += offsetX;

      // 避免出现横向滚动条
      this.$nextTick(() => {
        if (modalEl.offsetWidth + modalEl.offsetLeft >= document.body.offsetWidth && offsetX > 0) {
          this.marginLeft -= offsetX;
        }
      });
    },

    lockDocBody() {
      // 禁止滚动穿透
      document.body.style.overflow = 'hidden';
    },

    unlockDocBody() {
      // 恢复body
      document.body.style.overflow = '';
    },
  },
};
</script>

<style lang="scss">
.plv-ia-c-modal {
  // z-index: 10001;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.6);
  overflow: auto;
  overflow: hidden;
  &--no-bg {
    width: auto;
    height: auto;
    .plv-ia-c-modal__content {
      position: fixed;
      margin: 0;
    }
  }
}

.plv-ia-c-modal__content {
  position: absolute;
  top: 50%;
  left: 50%;
  margin-left: -180px;
  padding-top: 50px;
  background-color: #2B2C36;
  width: 280px;
  // min-height: 400px;
  box-sizing: border-box;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
}

.plv-ia-c-modal__close {
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  height: 50px;
  color: #90A4AE;
  cursor: pointer;
  text-align: center;
  font-size: 20px;
  line-height: 51px;
  transition: all .3s ease-in-out;
  border: none;
  outline: none;
  background-color: transparent;
  background-image: url(./imgs/icon-close-normal.png);
  background-size: 14px;
  background-repeat: no-repeat;
  background-position: center;
}

.plv-ia-c-modal__header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 48px;
  padding: 0 18px;
  box-sizing: border-box;
  background: #3E3E4E;

  h2 {
    padding: 0;
    padding-left: 2px;
    margin: 0;
    font-weight: normal;
    line-height: 48px;
    font-size: 14px;
    color: #fff;
    user-select: none;
  }
}

.plv-ia-c-modal__body {
  &--border {
    overflow: auto;
    border-bottom: 1px solid #ECEFF1;
  }
}

.plv-ia-c-modal__footer {
  position: absolute;
  bottom: 0;
  width: 100%;
}

.modal-fade-enter-active {
  animation: modal-fade-in .3s;
}

.modal-fade-leave-active {
  animation: modal-fade-out .3s;
}

@keyframes modal-fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes modal-fade-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>
